/**
 * Trip CO₂ Calculator — signup call-to-action button.
 *
 * Replaced the old `WaitlistEmailForm` when the waitlist sunset
 * (Aug 2026, Jody is live): instead of capturing an email for later,
 * every conversion surface now sends the visitor straight to account
 * creation. Used in three places on the calculator page:
 *   • Inside the Meet Jody CTA (source: 'travel_impact_calculator_main'
 *     or '..._quota_gate' when mounted under the quota banner)
 *   • Inside the Plan-B "Can't take trains?" dialog
 *     (source: 'travel_impact_calculator_cant_train_dialog')
 *
 * Fires the `travel_impact_calculator_signup_click` Mixpanel event on
 * click, then navigates to /signup client-side (SPA navigation, so the
 * queued event still flushes). The signup itself is measured
 * separately: `Signup Frontend` in Mixpanel and the Google Ads
 * conversion, both fired from the auth flow on account creation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics } from '../../shared/analytics';

export interface SignupCTAButtonProps {
  /**
   * Analytics tag identifying *which* entry point the user came
   * through. Carried on the `travel_impact_calculator_signup_click`
   * event so the funnel can compare surfaces.
   */
  source:
    | 'travel_impact_calculator_main'
    | 'travel_impact_calculator_cant_train_dialog'
    | 'travel_impact_calculator_quota_gate';
  /** Override the button label per surface. */
  label?: string;
  /**
   * Extra event properties attached to the click event. Forwarded
   * from the calculator page so the event carries trip context
   * (origin, destination, mode, roundTrip, co2eKg, distanceKm) on
   * top of Mixpanel's auto-properties. `source` is added
   * automatically and does not need to be in here.
   */
  trackingProps?: Record<string, unknown>;
}

const SignupCTAButton: React.FC<SignupCTAButtonProps> = ({
  source,
  label = 'Start your next adventure →',
  trackingProps,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    analytics.trackTravelImpactCalculatorSignupClick({
      source,
      ...(trackingProps ?? {}),
    });
    navigate('/signup');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        // Same visual weight as the old form's submit button: white
        // pill on the emerald-gradient surfaces, bold emerald text.
        width: '100%',
        padding: '0.8rem 1rem',
        fontSize: 15,
        fontWeight: 800,
        color: '#047857',
        background: '#fff',
        border: 0,
        borderRadius: 8,
        cursor: 'pointer',
        letterSpacing: -0.2,
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {label}
    </button>
  );
};

export default SignupCTAButton;
